'use client';
import React, { useState } from 'react';
import AppLayout from '@/components/AppLayout';
import Icon from '@/components/ui/AppIcon';
import { useTheme } from '@/context/ThemeContext';
import ZohoSyncDashboard from '@/components/ZohoSyncDashboard';
import ZohoFieldMapping from '@/components/ZohoFieldMapping';

const SETTINGS_SECTIONS = [
  { id: 'general', label: 'General', icon: 'Cog6ToothIcon' },
  { id: 'notifications', label: 'Notifications', icon: 'BellIcon' },
  { id: 'team', label: 'Team & Roles', icon: 'UserGroupIcon' },
  { id: 'margin', label: 'Margin Rules', icon: 'CurrencyDollarIcon' },
  { id: 'templates', label: 'Templates', icon: 'DocumentDuplicateIcon' },
  { id: 'libraries', label: 'Libraries', icon: 'BookOpenIcon' },
  { id: 'zoho', label: 'Zoho CRM', icon: 'LinkIcon' },
  { id: 'zoho-sync', label: 'Zoho Sync', icon: 'CloudArrowUpIcon' },
  { id: 'field-mapping', label: 'Field Mapping', icon: 'TableCellsIcon' },
];

const LIBRARY_TABS = [
  { id: 'experience', label: 'Experience Records', icon: 'StarIcon' },
  { id: 'partners', label: 'Partner Logos', icon: 'RectangleGroupIcon' },
  { id: 'clients', label: 'Client References', icon: 'UserGroupIcon' },
  { id: 'tnc', label: 'T&C Templates', icon: 'ScaleIcon' },
  { id: 'aboutus', label: 'About Us', icon: 'BuildingOfficeIcon' },
];

const INIT_EXPERIENCE = [
  { id: 'e1', project: 'CPL Network Upgrade', client: 'Children Public Library', year: '2024', value: '15,000 OMR', scope: 'Supply + Installation' },
  { id: 'e2', project: 'ADNOC Data Center', client: 'ADNOC HQ', year: '2023', value: '142,000 OMR', scope: 'Design + Supply + Integration' },
  { id: 'e3', project: 'Etisalat Security Systems', client: 'Etisalat Group', year: '2023', value: '89,000 OMR', scope: 'Supply + Configuration' },
  { id: 'e4', project: 'Dubai Municipality Cloud', client: 'Dubai Municipality', year: '2022', value: '215,000 OMR', scope: 'Migration + Support' },
  { id: 'e5', project: 'Oman LNG Cooling Units', client: 'Oman LNG', year: '2022', value: '320,000 OMR', scope: 'Supply + Installation + Commissioning' },
];

const INIT_PARTNERS = [
  { id: 'p1', name: 'Cisco', category: 'Networking', tier: 'Gold', color: '#049FD9' },
  { id: 'p2', name: 'HP', category: 'Hardware', tier: 'Silver', color: '#0096D6' },
  { id: 'p3', name: 'Dell', category: 'Hardware', tier: 'Silver', color: '#007DB8' },
  { id: 'p4', name: 'Microsoft', category: 'Software', tier: 'Gold', color: '#00A4EF' },
  { id: 'p5', name: 'Huawei', category: 'Networking', tier: 'Silver', color: '#CF0A2C' },
  { id: 'p6', name: 'Fortinet', category: 'Security', tier: 'Gold', color: '#EE3124' },
];

const INIT_CLIENTS = [
  { id: 'c1', name: 'ADNOC', sector: 'Energy', type: 'Enterprise', since: '2021' },
  { id: 'c2', name: 'Etisalat', sector: 'Telecom', type: 'Enterprise', since: '2022' },
  { id: 'c3', name: 'Dubai Municipality', sector: 'Government', type: 'Government', since: '2020' },
  { id: 'c4', name: 'Children Public Library', sector: 'Education', type: 'Government', since: '2023' },
  { id: 'c5', name: 'Oman LNG', sector: 'Energy', type: 'Enterprise', since: '2022' },
];

const INIT_TNC = [
  { id: 't1', name: 'Standard IT Supply', content: 'All prices valid for 30 days.\nDelivery subject to vendor availability.\nManufacturer warranty applies.\nInstallation as per BOQ only.\nAdditional requirements quoted separately.' },
  { id: 't2', name: 'Government Projects', content: 'Prices valid for 60 days per government procurement policy.\nAll equipment must meet local regulatory standards.\nPayment within 45 days of invoice.\nRetention: 5% for 12 months post-handover.' },
  { id: 't3', name: 'Maintenance Contracts', content: 'SLA response time: 4 hours critical, 8 hours standard.\nPreventive maintenance: quarterly visits included.\nSpare parts coverage as per attached schedule.\nContract renewable annually.' },
];

// ─── Zoho OAuth Step Types ───────────────────────────────────────────────────
type ZohoStep = 'credentials' | 'authorize' | 'exchange' | 'done';

interface ZohoOAuthState {
  clientId: string;
  clientSecret: string;
  dataCenter: string;
  grantCode: string;
  refreshToken: string;
}

export default function SettingsPage() {
  const { theme, toggleTheme } = useTheme();
  const isLight = theme === 'light';
  const [activeSection, setActiveSection] = useState('general');
  const [activeLibTab, setActiveLibTab] = useState('experience');

  // Zoho OAuth state
  const [zohoStep, setZohoStep] = useState<ZohoStep>('credentials');
  const [zohoState, setZohoState] = useState<ZohoOAuthState>({
    clientId: '',
    clientSecret: '',
    dataCenter: 'https://accounts.zoho.com',
    grantCode: '',
    refreshToken: '',
  });
  const [zohoLoading, setZohoLoading] = useState(false);
  const [zohoError, setZohoError] = useState('');
  const [zohoVerified, setZohoVerified] = useState(false);
  const [zohoVerifying, setZohoVerifying] = useState(false);

  // Library state
  const [experienceRecords, setExperienceRecords] = useState(INIT_EXPERIENCE);
  const [partners, setPartners] = useState(INIT_PARTNERS);
  const [clients, setClients] = useState(INIT_CLIENTS);
  const [tncTemplates, setTncTemplates] = useState(INIT_TNC);
  const [aboutUsContent, setAboutUsContent] = useState('eCity Technologies LLC is a leading ICT solutions provider established in 2010, specializing in network infrastructure, security systems, and cloud solutions. ISO 9001:2015 certified with over 200 successful projects across the GCC region.\n\nOur team of certified engineers delivers end-to-end solutions from design through implementation and ongoing support.');
  const [editingTnc, setEditingTnc] = useState<string | null>(null);
  const [showAddExp, setShowAddExp] = useState(false);
  const [newExp, setNewExp] = useState({ project: '', client: '', year: '', value: '', scope: '' });

  const cardBg = isLight ? '#FFFFFF' : '#0B1628';
  const borderColor = isLight ? '#E2E8F0' : 'rgba(30,144,255,0.12)';
  const mutedText = isLight ? '#64748B' : '#6B7FA3';
  const headingText = isLight ? '#0F172A' : '#FFFFFF';
  const inputBg = isLight ? '#F8FAFC' : '#0D1829';

  const inputStyle = {
    background: inputBg,
    border: `1px solid ${borderColor}`,
    borderRadius: 8,
    padding: '8px 12px',
    color: headingText,
    fontSize: 12,
    outline: 'none',
    width: '100%',
  };

  const addExperienceRecord = () => {
    if (!newExp.project) return;
    setExperienceRecords(prev => [...prev, { id: `e${Date.now()}`, ...newExp }]);
    setNewExp({ project: '', client: '', year: '', value: '', scope: '' });
    setShowAddExp(false);
  };

  const removeExperience = (id: string) => setExperienceRecords(prev => prev.filter(r => r.id !== id));
  const removePartner = (id: string) => setPartners(prev => prev.filter(p => p.id !== id));
  const removeClient = (id: string) => setClients(prev => prev.filter(c => c.id !== id));
  const removeTnc = (id: string) => setTncTemplates(prev => prev.filter(t => t.id !== id));

  const DATA_CENTERS = [
    { label: 'Global (US)', value: 'https://accounts.zoho.com' },
    { label: 'Europe (EU)', value: 'https://accounts.zoho.eu' },
    { label: 'India (IN)', value: 'https://accounts.zoho.in' },
    { label: 'Australia (AU)', value: 'https://accounts.zoho.com.au' },
    { label: 'Japan (JP)', value: 'https://accounts.zoho.jp' },
  ];

  const getRedirectUri = () => {
    const siteUrl = process.env.NEXT_PUBLIC_SITE_URL || (typeof window !== 'undefined' ? window.location.origin : '');
    return `${siteUrl}/settings`;
  };

  const buildAuthUrl = () => {
    const base = zohoState.dataCenter;
    const params = new URLSearchParams({
      scope: 'ZohoCRM.modules.ALL',
      client_id: zohoState.clientId,
      response_type: 'code',
      access_type: 'offline',
      redirect_uri: getRedirectUri(),
    });
    return `${base}/oauth/v2/auth?${params.toString()}`;
  };

  const handleExchangeToken = async () => {
    if (!zohoState.grantCode.trim()) {
      setZohoError('Please paste the grant code from the URL.');
      return;
    }
    setZohoLoading(true);
    setZohoError('');
    try {
      const res = await fetch('/api/zoho/exchange-token', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          code: zohoState.grantCode.trim(),
          clientId: zohoState.clientId,
          clientSecret: zohoState.clientSecret,
          redirectUri: getRedirectUri(),
        }),
      });
      const data = await res.json();
      if (data.error) {
        setZohoError(`Token exchange failed: ${data.error}${data.description ? ' — ' + data.description : ''}`);
      } else {
        setZohoState(prev => ({ ...prev, refreshToken: data.refresh_token || '' }));
        setZohoStep('done');
      }
    } catch (err: any) {
      setZohoError(err.message || 'Token exchange failed');
    } finally {
      setZohoLoading(false);
    }
  };

  const handleVerifyCredentials = async () => {
    if (!zohoState.clientId || !zohoState.clientSecret || !zohoState.refreshToken) {
      setZohoError('Please fill in all credential fields.');
      return;
    }
    setZohoVerifying(true);
    setZohoError('');
    setZohoVerified(false);
    try {
      const res = await fetch('/api/zoho/verify-credentials', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          clientId: zohoState.clientId,
          clientSecret: zohoState.clientSecret,
          refreshToken: zohoState.refreshToken,
        }),
      });
      const data = await res.json();
      if (data.valid) {
        setZohoVerified(true);
      } else {
        setZohoError(`Verification failed: ${data.error || 'Invalid credentials'}`);
      }
    } catch (err: any) {
      setZohoError(err.message || 'Verification failed');
    } finally {
      setZohoVerifying(false);
    }
  };

  const renderLibraries = () => (
    <div className="space-y-4">
      <div className="rounded-xl overflow-hidden" style={{ background: cardBg, border: `1px solid ${borderColor}` }}>
        {/* Library sub-tabs */}
        <div className="flex overflow-x-auto" style={{ borderBottom: `1px solid ${borderColor}`, background: inputBg }}>
          {LIBRARY_TABS.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveLibTab(tab.id)}
              className="flex items-center gap-1.5 px-4 py-3 text-xs font-600 whitespace-nowrap transition-all border-b-2"
              style={{
                color: activeLibTab === tab.id ? '#1E90FF' : mutedText,
                borderBottomColor: activeLibTab === tab.id ? '#1E90FF' : 'transparent',
                background: activeLibTab === tab.id ? 'rgba(30,144,255,0.06)' : 'transparent',
              }}
            >
              <Icon name={tab.icon as any} size={13} />
              {tab.label}
            </button>
          ))}
        </div>

        <div className="p-5">
          {/* Experience Records */}
          {activeLibTab === 'experience' && (
            <div className="space-y-3">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <p className="text-sm font-700" style={{ color: headingText }}>Experience Records</p>
                  <p className="text-xs mt-0.5" style={{ color: mutedText }}>Past projects shown in proposal "Our Experience" page</p>
                </div>
                <button
                  onClick={() => setShowAddExp(!showAddExp)}
                  className="flex items-center gap-1.5 text-xs px-3 py-2 rounded-lg font-600"
                  style={{ background: 'rgba(30,144,255,0.1)', color: '#1E90FF', border: '1px solid rgba(30,144,255,0.25)' }}
                >
                  <Icon name="PlusIcon" size={13} />
                  Add Record
                </button>
              </div>

              {showAddExp && (
                <div className="rounded-xl p-4 space-y-3 mb-4" style={{ background: inputBg, border: `1px solid rgba(30,144,255,0.2)` }}>
                  <p className="text-xs font-700" style={{ color: '#1E90FF' }}>New Experience Record</p>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { key: 'project', label: 'Project Name' },
                      { key: 'client', label: 'Client' },
                      { key: 'year', label: 'Year' },
                      { key: 'value', label: 'Value (OMR)' },
                      { key: 'scope', label: 'Scope' },
                    ].map(({ key, label }) => (
                      <div key={key} className={key === 'scope' ? 'col-span-2' : ''}>
                        <label className="text-[10px] font-600 mb-1 block" style={{ color: mutedText }}>{label}</label>
                        <input
                          value={(newExp as any)[key]}
                          onChange={e => setNewExp(prev => ({ ...prev, [key]: e.target.value }))}
                          style={inputStyle}
                          placeholder={label}
                        />
                      </div>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <button onClick={addExperienceRecord} className="btn-primary text-xs px-4 py-2">Save Record</button>
                    <button onClick={() => setShowAddExp(false)} className="btn-secondary text-xs px-4 py-2">Cancel</button>
                  </div>
                </div>
              )}

              <div className="rounded-lg overflow-hidden" style={{ border: `1px solid ${borderColor}` }}>
                <table className="w-full text-xs">
                  <thead>
                    <tr style={{ background: inputBg }}>
                      {['Project', 'Client', 'Year', 'Value', 'Scope', ''].map(h => (
                        <th key={h} className="text-left px-3 py-2.5 font-600" style={{ color: mutedText }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {experienceRecords.map((rec, i) => (
                      <tr key={rec.id} style={{ borderTop: `1px solid ${borderColor}`, background: i % 2 === 0 ? 'transparent' : (isLight ? '#F8FAFC' : 'rgba(30,144,255,0.02)') }}>
                        <td className="px-3 py-2.5 font-600" style={{ color: headingText }}>{rec.project}</td>
                        <td className="px-3 py-2.5" style={{ color: mutedText }}>{rec.client}</td>
                        <td className="px-3 py-2.5" style={{ color: mutedText }}>{rec.year}</td>
                        <td className="px-3 py-2.5 font-600" style={{ color: '#1E90FF' }}>{rec.value}</td>
                        <td className="px-3 py-2.5" style={{ color: mutedText }}>{rec.scope}</td>
                        <td className="px-3 py-2.5">
                          <button onClick={() => removeExperience(rec.id)} className="p-1 rounded hover:bg-red-500/10 transition-all" style={{ color: '#FF4757' }}>
                            <Icon name="TrashIcon" size={12} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Partner Logos */}
          {activeLibTab === 'partners' && (
            <div className="space-y-3">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <p className="text-sm font-700" style={{ color: headingText }}>Partner Logos</p>
                  <p className="text-xs mt-0.5" style={{ color: mutedText }}>Technology and brand partners shown in proposals</p>
                </div>
                <button className="flex items-center gap-1.5 text-xs px-3 py-2 rounded-lg font-600" style={{ background: 'rgba(30,144,255,0.1)', color: '#1E90FF', border: '1px solid rgba(30,144,255,0.25)' }}>
                  <Icon name="PlusIcon" size={13} />
                  Add Partner
                </button>
              </div>
              <div className="grid grid-cols-3 gap-3">
                {partners.map(partner => (
                  <div key={partner.id} className="rounded-xl p-4 relative group" style={{ background: inputBg, border: `1px solid ${borderColor}` }}>
                    <button
                      onClick={() => removePartner(partner.id)}
                      className="absolute top-2 right-2 p-1 rounded opacity-0 group-hover:opacity-100 transition-all"
                      style={{ background: 'rgba(255,71,87,0.1)', color: '#FF4757' }}
                    >
                      <Icon name="XMarkIcon" size={11} />
                    </button>
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-2" style={{ background: `${partner.color}20` }}>
                      <span className="text-sm font-800" style={{ color: partner.color }}>{partner.name.slice(0, 2)}</span>
                    </div>
                    <p className="text-xs font-700" style={{ color: headingText }}>{partner.name}</p>
                    <p className="text-[10px]" style={{ color: mutedText }}>{partner.category}</p>
                    <span className="text-[9px] px-1.5 py-0.5 rounded-full mt-1 inline-block" style={{ background: partner.tier === 'Gold' ? 'rgba(245,158,11,0.15)' : 'rgba(107,127,163,0.15)', color: partner.tier === 'Gold' ? '#F59E0B' : mutedText }}>
                      {partner.tier}
                    </span>
                  </div>
                ))}
                <div className="rounded-xl p-4 flex flex-col items-center justify-center cursor-pointer transition-all" style={{ background: 'transparent', border: `1px dashed ${borderColor}`, minHeight: 100 }}>
                  <Icon name="PlusIcon" size={20} style={{ color: mutedText } as React.CSSProperties} />
                  <p className="text-[10px] mt-1" style={{ color: mutedText }}>Upload Logo</p>
                </div>
              </div>
            </div>
          )}

          {/* Client References */}
          {activeLibTab === 'clients' && (
            <div className="space-y-3">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <p className="text-sm font-700" style={{ color: headingText }}>Client References</p>
                  <p className="text-xs mt-0.5" style={{ color: mutedText }}>Customer logos and references shown in proposals</p>
                </div>
                <button className="flex items-center gap-1.5 text-xs px-3 py-2 rounded-lg font-600" style={{ background: 'rgba(30,144,255,0.1)', color: '#1E90FF', border: '1px solid rgba(30,144,255,0.25)' }}>
                  <Icon name="PlusIcon" size={13} />
                  Add Client
                </button>
              </div>
              <div className="space-y-2">
                {clients.map((client, i) => (
                  <div key={client.id} className="flex items-center gap-3 px-4 py-3 rounded-lg" style={{ background: i % 2 === 0 ? inputBg : 'transparent', border: `1px solid ${borderColor}` }}>
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: 'rgba(30,144,255,0.1)' }}>
                      <span className="text-xs font-800" style={{ color: '#1E90FF' }}>{client.name.slice(0, 2)}</span>
                    </div>
                    <div className="flex-1">
                      <p className="text-xs font-600" style={{ color: headingText }}>{client.name}</p>
                      <p className="text-[10px]" style={{ color: mutedText }}>{client.sector} · {client.type} · Since {client.since}</p>
                    </div>
                    <span className="text-[10px] px-2 py-0.5 rounded-full" style={{ background: client.type === 'Government' ? 'rgba(0,214,143,0.1)' : 'rgba(30,144,255,0.1)', color: client.type === 'Government' ? '#00D68F' : '#1E90FF' }}>
                      {client.type}
                    </span>
                    <button onClick={() => removeClient(client.id)} className="p-1 rounded hover:bg-red-500/10 transition-all" style={{ color: '#FF4757' }}>
                      <Icon name="TrashIcon" size={12} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* T&C Templates */}
          {activeLibTab === 'tnc' && (
            <div className="space-y-3">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <p className="text-sm font-700" style={{ color: headingText }}>T&C Templates</p>
                  <p className="text-xs mt-0.5" style={{ color: mutedText }}>Reusable terms and conditions for proposals</p>
                </div>
                <button
                  onClick={() => setTncTemplates(prev => [...prev, { id: `t${Date.now()}`, name: 'New Template', content: '' }])}
                  className="flex items-center gap-1.5 text-xs px-3 py-2 rounded-lg font-600"
                  style={{ background: 'rgba(30,144,255,0.1)', color: '#1E90FF', border: '1px solid rgba(30,144,255,0.25)' }}
                >
                  <Icon name="PlusIcon" size={13} />
                  New Template
                </button>
              </div>
              <div className="space-y-3">
                {tncTemplates.map(tmpl => (
                  <div key={tmpl.id} className="rounded-xl overflow-hidden" style={{ border: `1px solid ${editingTnc === tmpl.id ? 'rgba(30,144,255,0.4)' : borderColor}` }}>
                    <div
                      className="flex items-center justify-between px-4 py-3 cursor-pointer"
                      style={{ background: editingTnc === tmpl.id ? 'rgba(30,144,255,0.06)' : inputBg }}
                      onClick={() => setEditingTnc(editingTnc === tmpl.id ? null : tmpl.id)}
                    >
                      <div className="flex items-center gap-2">
                        <Icon name="ScaleIcon" size={14} style={{ color: '#1E90FF' } as React.CSSProperties} />
                        <span className="text-xs font-600" style={{ color: headingText }}>{tmpl.name}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <button onClick={e => { e.stopPropagation(); removeTnc(tmpl.id); }} className="p-1 rounded hover:bg-red-500/10 transition-all" style={{ color: '#FF4757' }}>
                          <Icon name="TrashIcon" size={12} />
                        </button>
                        <Icon name={editingTnc === tmpl.id ? 'ChevronUpIcon' : 'ChevronDownIcon'} size={13} style={{ color: mutedText } as React.CSSProperties} />
                      </div>
                    </div>
                    {editingTnc === tmpl.id && (
                      <div className="p-4 space-y-2">
                        <input
                          value={tmpl.name}
                          onChange={e => setTncTemplates(prev => prev.map(t => t.id === tmpl.id ? { ...t, name: e.target.value } : t))}
                          style={{ ...inputStyle, marginBottom: 8 }}
                          placeholder="Template name"
                        />
                        <textarea
                          rows={6}
                          value={tmpl.content}
                          onChange={e => setTncTemplates(prev => prev.map(t => t.id === tmpl.id ? { ...t, content: e.target.value } : t))}
                          style={{ ...inputStyle, resize: 'vertical' as const }}
                          placeholder="Enter terms and conditions..."
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* About Us */}
          {activeLibTab === 'aboutus' && (
            <div className="space-y-4">
              <div>
                <p className="text-sm font-700 mb-1" style={{ color: headingText }}>About Us Content</p>
                <p className="text-xs" style={{ color: mutedText }}>Default company description used in all proposals. Can be overridden per proposal.</p>
              </div>
              <textarea
                rows={8}
                value={aboutUsContent}
                onChange={e => setAboutUsContent(e.target.value)}
                style={{ ...inputStyle, resize: 'vertical' as const, padding: '12px' }}
                placeholder="Enter company description..."
              />
              <div className="grid grid-cols-3 gap-3">
                {[
                  { label: 'Company Name', value: 'eCity Technologies LLC' },
                  { label: 'Founded', value: '2010' },
                  { label: 'Certification', value: 'ISO 9001:2015' },
                  { label: 'Projects Delivered', value: '200+' },
                  { label: 'Countries', value: 'GCC Region' },
                  { label: 'Employees', value: '150+' },
                ].map(({ label, value }) => (
                  <div key={label} className="rounded-lg p-3" style={{ background: inputBg, border: `1px solid ${borderColor}` }}>
                    <p className="text-[10px] mb-1" style={{ color: mutedText }}>{label}</p>
                    <input
                      defaultValue={value}
                      className="text-xs font-600 w-full bg-transparent outline-none"
                      style={{ color: headingText }}
                    />
                  </div>
                ))}
              </div>
              <button className="btn-primary text-xs px-5 py-2">Save About Us</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const renderZohoSection = () => (
    <div className="space-y-4">
      {/* Header card */}
      <div className="rounded-xl p-5" style={{ background: cardBg, border: `1px solid ${borderColor}` }}>
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: 'rgba(30,144,255,0.12)', border: '1px solid rgba(30,144,255,0.25)' }}>
            <Icon name="LinkIcon" size={18} style={{ color: '#1E90FF' } as React.CSSProperties} />
          </div>
          <div className="flex-1">
            <h3 className="text-sm font-700" style={{ color: headingText }}>Zoho CRM Integration</h3>
            <p className="text-xs mt-1" style={{ color: mutedText }}>
              Connect ProposalLink to your Zoho CRM account. This wizard walks you through the OAuth2 token exchange so you can sync customers and proposals automatically.
            </p>
          </div>
          {zohoStep === 'done' && (
            <span className="flex items-center gap-1 text-[10px] px-2.5 py-1 rounded-full font-700 shrink-0" style={{ background: 'rgba(0,214,143,0.12)', color: '#00D68F', border: '1px solid rgba(0,214,143,0.25)' }}>
              <Icon name="CheckCircleIcon" size={11} />
              Connected
            </span>
          )}
        </div>

        {/* Step indicator */}
        <div className="flex items-center gap-0 mt-5">
          {(['credentials', 'authorize', 'exchange', 'done'] as ZohoStep[]).map((step, idx) => {
            const labels = ['Credentials', 'Authorize', 'Exchange', 'Done'];
            const stepOrder: ZohoStep[] = ['credentials', 'authorize', 'exchange', 'done'];
            const currentIdx = stepOrder.indexOf(zohoStep);
            const isActive = step === zohoStep;
            const isDone = stepOrder.indexOf(step) < currentIdx;
            return (
              <React.Fragment key={step}>
                <div className="flex flex-col items-center">
                  <div
                    className="w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-700 transition-all"
                    style={{
                      background: isDone ? '#00D68F' : isActive ? '#1E90FF' : (isLight ? '#E2E8F0' : 'rgba(30,144,255,0.1)'),
                      color: isDone || isActive ? '#fff' : mutedText,
                      border: isActive ? '2px solid rgba(30,144,255,0.4)' : '2px solid transparent',
                    }}
                  >
                    {isDone ? <Icon name="CheckIcon" size={11} /> : idx + 1}
                  </div>
                  <span className="text-[9px] mt-1 font-600" style={{ color: isActive ? '#1E90FF' : isDone ? '#00D68F' : mutedText }}>{labels[idx]}</span>
                </div>
                {idx < 3 && (
                  <div className="flex-1 h-0.5 mx-1 mb-4 rounded-full" style={{ background: stepOrder.indexOf(step) < currentIdx ? '#00D68F' : (isLight ? '#E2E8F0' : 'rgba(30,144,255,0.1)') }} />
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {/* Error banner */}
      {zohoError && (
        <div className="rounded-xl px-4 py-3 flex items-start gap-2.5" style={{ background: 'rgba(255,71,87,0.08)', border: '1px solid rgba(255,71,87,0.25)' }}>
          <Icon name="ExclamationCircleIcon" size={14} style={{ color: '#FF4757', marginTop: 1, flexShrink: 0 } as React.CSSProperties} />
          <p className="text-xs" style={{ color: '#FF4757' }}>{zohoError}</p>
        </div>
      )}

      {/* ── Step 1: Credentials ── */}
      {zohoStep === 'credentials' && (
        <div className="rounded-xl p-5 space-y-4" style={{ background: cardBg, border: `1px solid ${borderColor}` }}>
          <div>
            <p className="text-sm font-700" style={{ color: headingText }}>Step 1 — Enter Your Zoho API Credentials</p>
            <p className="text-xs mt-1" style={{ color: mutedText }}>
              Go to <span className="font-600" style={{ color: '#1E90FF' }}>api-console.zoho.com</span> → create a <strong>Server-based Application</strong> → copy your Client ID and Client Secret.
            </p>
          </div>

          <div className="space-y-3">
            <div>
              <label className="text-[10px] font-700 mb-1.5 block uppercase tracking-wide" style={{ color: mutedText }}>Client ID</label>
              <input
                value={zohoState.clientId}
                onChange={e => setZohoState(prev => ({ ...prev, clientId: e.target.value }))}
                style={inputStyle}
                placeholder="1000.XXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXX"
              />
            </div>
            <div>
              <label className="text-[10px] font-700 mb-1.5 block uppercase tracking-wide" style={{ color: mutedText }}>Client Secret</label>
              <input
                type="password"
                value={zohoState.clientSecret}
                onChange={e => setZohoState(prev => ({ ...prev, clientSecret: e.target.value }))}
                style={inputStyle}
                placeholder="••••••••••••••••••••••••••••••••"
              />
            </div>
            <div>
              <label className="text-[10px] font-700 mb-1.5 block uppercase tracking-wide" style={{ color: mutedText }}>Data Center / Region</label>
              <select
                value={zohoState.dataCenter}
                onChange={e => setZohoState(prev => ({ ...prev, dataCenter: e.target.value }))}
                style={{ ...inputStyle, cursor: 'pointer' }}
              >
                {DATA_CENTERS.map(dc => (
                  <option key={dc.value} value={dc.value}>{dc.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="rounded-lg p-3" style={{ background: inputBg, border: `1px solid ${borderColor}` }}>
            <p className="text-[10px] font-700 mb-1" style={{ color: mutedText }}>Authorized Redirect URI (add this in Zoho API Console)</p>
            <p className="text-xs font-600 break-all" style={{ color: '#1E90FF' }}>
              {typeof window !== 'undefined' ? `${window.location.origin}/settings` : `${process.env.NEXT_PUBLIC_SITE_URL || ''}/settings`}
            </p>
          </div>

          <button
            onClick={() => {
              if (!zohoState.clientId.trim() || !zohoState.clientSecret.trim()) {
                setZohoError('Client ID and Client Secret are required.');
                return;
              }
              setZohoError('');
              setZohoStep('authorize');
            }}
            className="btn-primary text-xs px-5 py-2.5 w-full"
          >
            Continue to Authorization
            <Icon name="ArrowRightIcon" size={13} />
          </button>
        </div>
      )}

      {/* ── Step 2: Authorize ── */}
      {zohoStep === 'authorize' && (
        <div className="rounded-xl p-5 space-y-4" style={{ background: cardBg, border: `1px solid ${borderColor}` }}>
          <div>
            <p className="text-sm font-700" style={{ color: headingText }}>Step 2 — Authorize ProposalLink in Zoho</p>
            <p className="text-xs mt-1" style={{ color: mutedText }}>
              Click the button below to open Zoho's authorization page. Log in and click <strong>Accept</strong>. You'll be redirected back here — copy the <code className="px-1 py-0.5 rounded text-[10px]" style={{ background: 'rgba(30,144,255,0.1)', color: '#1E90FF' }}>code</code> parameter from the URL.
            </p>
          </div>

          <div className="rounded-lg p-3 space-y-1" style={{ background: inputBg, border: `1px solid ${borderColor}` }}>
            <p className="text-[10px] font-700" style={{ color: mutedText }}>Authorization URL Preview</p>
            <p className="text-[10px] break-all font-500" style={{ color: mutedText }}>{buildAuthUrl()}</p>
          </div>

          <a
            href={buildAuthUrl()}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-2 text-xs font-700 px-5 py-2.5 rounded-xl transition-all w-full"
            style={{ background: '#1E90FF', color: '#fff' }}
          >
            <Icon name="ArrowTopRightOnSquareIcon" size={13} />
            Open Zoho Authorization Page
          </a>

          <div className="flex gap-2">
            <button
              onClick={() => { setZohoError(''); setZohoStep('credentials'); }}
              className="btn-secondary text-xs px-4 py-2"
            >
              <Icon name="ArrowLeftIcon" size={12} />
              Back
            </button>
            <button
              onClick={() => { setZohoError(''); setZohoStep('exchange'); }}
              className="btn-primary text-xs px-5 py-2 flex-1"
            >
              I've Authorized — Enter Grant Code
              <Icon name="ArrowRightIcon" size={13} />
            </button>
          </div>
        </div>
      )}

      {/* ── Step 3: Exchange ── */}
      {zohoStep === 'exchange' && (
        <div className="rounded-xl p-5 space-y-4" style={{ background: cardBg, border: `1px solid ${borderColor}` }}>
          <div>
            <p className="text-sm font-700" style={{ color: headingText }}>Step 3 — Exchange Grant Code for Refresh Token</p>
            <p className="text-xs mt-1" style={{ color: mutedText }}>
              After authorizing, Zoho redirected you back to this page with a URL like:<br />
              <code className="text-[10px] px-1 py-0.5 rounded" style={{ background: 'rgba(30,144,255,0.1)', color: '#1E90FF' }}>
                /settings?code=<strong>1000.XXXXXXXX</strong>&location=us&accounts-server=...
              </code><br />
              Copy the value after <code className="text-[10px] px-1 py-0.5 rounded" style={{ background: 'rgba(30,144,255,0.1)', color: '#1E90FF' }}>code=</code> and paste it below.
            </p>
          </div>

          <div>
            <label className="text-[10px] font-700 mb-1.5 block uppercase tracking-wide" style={{ color: mutedText }}>Grant Code</label>
            <input
              value={zohoState.grantCode}
              onChange={e => setZohoState(prev => ({ ...prev, grantCode: e.target.value }))}
              style={inputStyle}
              placeholder="1000.XXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXX.XXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXX"
            />
          </div>

          <div className="flex gap-2">
            <button
              onClick={() => { setZohoError(''); setZohoStep('authorize'); }}
              className="btn-secondary text-xs px-4 py-2"
            >
              <Icon name="ArrowLeftIcon" size={12} />
              Back
            </button>
            <button
              onClick={handleExchangeToken}
              disabled={zohoLoading}
              className="btn-primary text-xs px-5 py-2 flex-1"
            >
              {zohoLoading ? (
                <>
                  <svg className="animate-spin" width={13} height={13} viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeDasharray="31.4" strokeDashoffset="10" />
                  </svg>
                  Exchanging Token…
                </>
              ) : (
                <>
                  Exchange for Refresh Token
                  <Icon name="ArrowRightIcon" size={13} />
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* ── Step 4: Done ── */}
      {zohoStep === 'done' && (
        <div className="rounded-xl p-5 space-y-4" style={{ background: cardBg, border: `1px solid ${borderColor}` }}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0" style={{ background: 'rgba(0,214,143,0.12)', border: '1px solid rgba(0,214,143,0.3)' }}>
              <Icon name="CheckCircleIcon" size={20} style={{ color: '#00D68F' } as React.CSSProperties} />
            </div>
            <div>
              <p className="text-sm font-700" style={{ color: headingText }}>Refresh Token Retrieved Successfully!</p>
              <p className="text-xs mt-0.5" style={{ color: mutedText }}>Copy the token below and add it to your environment variables.</p>
            </div>
          </div>

          <div className="rounded-xl p-4 space-y-3" style={{ background: inputBg, border: `1px solid rgba(0,214,143,0.2)` }}>
            <p className="text-[10px] font-700 uppercase tracking-wide" style={{ color: '#00D68F' }}>Your Refresh Token</p>
            <div className="flex items-center gap-2">
              <code className="flex-1 text-[11px] break-all font-600 p-2 rounded-lg" style={{ background: cardBg, color: headingText, border: `1px solid ${borderColor}` }}>
                {zohoState.refreshToken}
              </code>
              <button
                onClick={() => navigator.clipboard.writeText(zohoState.refreshToken)}
                className="p-2 rounded-lg shrink-0 transition-all"
                style={{ background: 'rgba(0,214,143,0.1)', color: '#00D68F', border: '1px solid rgba(0,214,143,0.25)' }}
                title="Copy to clipboard"
              >
                <Icon name="ClipboardDocumentIcon" size={14} />
              </button>
            </div>
          </div>

          <div className="rounded-xl p-4 space-y-2" style={{ background: inputBg, border: `1px solid ${borderColor}` }}>
            <p className="text-[10px] font-700 uppercase tracking-wide mb-2" style={{ color: mutedText }}>Add these to your .env file</p>
            {[
              { key: 'ZOHO_CLIENT_ID', value: zohoState.clientId },
              { key: 'ZOHO_CLIENT_SECRET', value: '(your client secret)' },
              { key: 'ZOHO_REFRESH_TOKEN', value: zohoState.refreshToken },
              { key: 'ZOHO_ACCOUNTS_URL', value: zohoState.dataCenter },
              { key: 'ZOHO_API_DOMAIN', value: zohoState.dataCenter.replace('accounts.', 'www.zohoapis.') },
            ].map(({ key, value }) => (
              <div key={key} className="flex items-center gap-2">
                <code className="text-[10px] font-700 shrink-0" style={{ color: '#1E90FF' }}>{key}=</code>
                <code className="text-[10px] flex-1 truncate" style={{ color: mutedText }}>{value}</code>
              </div>
            ))}
          </div>

          {/* Verify existing credentials */}
          <div className="rounded-xl p-4 space-y-3" style={{ background: inputBg, border: `1px solid ${borderColor}` }}>
            <p className="text-xs font-700" style={{ color: headingText }}>Verify Saved Credentials</p>
            <p className="text-[10px]" style={{ color: mutedText }}>After updating your .env file, click below to confirm the connection works.</p>
            <div className="space-y-2">
              <div>
                <label className="text-[10px] font-700 mb-1 block" style={{ color: mutedText }}>Refresh Token to Test</label>
                <input
                  value={zohoState.refreshToken}
                  onChange={e => setZohoState(prev => ({ ...prev, refreshToken: e.target.value }))}
                  style={inputStyle}
                  placeholder="Paste refresh token…"
                />
              </div>
            </div>
            <button
              onClick={handleVerifyCredentials}
              disabled={zohoVerifying}
              className="flex items-center gap-2 text-xs font-700 px-4 py-2 rounded-xl transition-all"
              style={{ background: zohoVerified ? 'rgba(0,214,143,0.12)' : 'rgba(30,144,255,0.1)', color: zohoVerified ? '#00D68F' : '#1E90FF', border: `1px solid ${zohoVerified ? 'rgba(0,214,143,0.3)' : 'rgba(30,144,255,0.25)'}` }}
            >
              {zohoVerifying ? (
                <>
                  <svg className="animate-spin" width={12} height={12} viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeDasharray="31.4" strokeDashoffset="10" />
                  </svg>
                  Verifying…
                </>
              ) : zohoVerified ? (
                <>
                  <Icon name="CheckCircleIcon" size={13} />
                  Connection Verified!
                </>
              ) : (
                <>
                  <Icon name="SignalIcon" size={13} />
                  Test Connection
                </>
              )}
            </button>
          </div>

          <button
            onClick={() => { setZohoStep('credentials'); setZohoState({ clientId: '', clientSecret: '', dataCenter: 'https://accounts.zoho.com', grantCode: '', refreshToken: '' }); setZohoError(''); setZohoVerified(false); }}
            className="btn-secondary text-xs px-4 py-2"
          >
            <Icon name="ArrowPathIcon" size={12} />
            Start Over
          </button>
        </div>
      )}

      {/* Help card */}
      <div className="rounded-xl p-4" style={{ background: isLight ? '#F0F9FF' : 'rgba(30,144,255,0.04)', border: `1px solid rgba(30,144,255,0.15)` }}>
        <p className="text-xs font-700 mb-2" style={{ color: '#1E90FF' }}>Quick Reference</p>
        <div className="space-y-1.5">
          {[
            { label: 'API Console', value: 'api-console.zoho.com' },
            { label: 'Required Scope', value: 'ZohoCRM.modules.ALL' },
            { label: 'App Type', value: 'Server-based Application' },
            { label: 'Grant Type', value: 'authorization_code → refresh_token' },
          ].map(({ label, value }) => (
            <div key={label} className="flex items-center gap-2">
              <span className="text-[10px] font-600 w-28 shrink-0" style={{ color: mutedText }}>{label}</span>
              <span className="text-[10px] font-600" style={{ color: headingText }}>{value}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderZohoSyncDashboard = () => (
    <ZohoSyncDashboard isLight={isLight} />
  );

  const renderFieldMapping = () => (
    <ZohoFieldMapping isLight={isLight} />
  );

  return (
    <AppLayout pageTitle="Settings">
      <div className="max-w-5xl">
        <div className="mb-6">
          <h1 className="text-xl font-700" style={{ color: headingText }}>Settings</h1>
          <p className="text-sm mt-1" style={{ color: mutedText }}>Configure your ProposalLink workspace</p>
        </div>

        <div className="flex gap-6">
          {/* Sidebar */}
          <div className="w-48 shrink-0 space-y-1">
            {SETTINGS_SECTIONS.map((s) => (
              <button
                key={s.id}
                onClick={() => setActiveSection(s.id)}
                className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-500 transition-all text-left"
                style={{
                  background: activeSection === s.id ? 'rgba(30,144,255,0.12)' : 'transparent',
                  color: activeSection === s.id ? '#1E90FF' : mutedText,
                  border: activeSection === s.id ? '1px solid rgba(30,144,255,0.25)' : '1px solid transparent',
                }}
              >
                <Icon name={s.icon as any} size={15} />
                {s.label}
                {s.id === 'libraries' && (
                  <span className="ml-auto text-[9px] px-1.5 py-0.5 rounded-full font-700" style={{ background: 'rgba(0,214,143,0.15)', color: '#00D68F' }}>NEW</span>
                )}
                {s.id === 'zoho' && (
                  <span className="ml-auto text-[9px] px-1.5 py-0.5 rounded-full font-700" style={{ background: 'rgba(30,144,255,0.15)', color: '#1E90FF' }}>CRM</span>
                )}
                {s.id === 'zoho-sync' && (
                  <span className="ml-auto text-[9px] px-1.5 py-0.5 rounded-full font-700" style={{ background: 'rgba(245,166,35,0.15)', color: '#F5A623' }}>SYNC</span>
                )}
                {s.id === 'field-mapping' && (
                  <span className="ml-auto text-[9px] px-1.5 py-0.5 rounded-full font-700" style={{ background: 'rgba(228,37,39,0.12)', color: '#E42527' }}>MAP</span>
                )}
              </button>
            ))}
          </div>

          {/* Content */}
          <div className="flex-1 space-y-4">
            {activeSection === 'general' && (
              <>
                <div className="rounded-xl p-5" style={{ background: cardBg, border: `1px solid ${borderColor}` }}>
                  <h3 className="text-sm font-700 mb-4" style={{ color: headingText }}>Appearance</h3>
                  <div className="flex items-center justify-between py-3" style={{ borderBottom: `1px solid ${borderColor}` }}>
                    <div>
                      <p className="text-sm font-600" style={{ color: headingText }}>Theme</p>
                      <p className="text-xs mt-0.5" style={{ color: mutedText }}>Switch between dark and light mode</p>
                    </div>
                    <button onClick={toggleTheme} className="btn-secondary text-xs px-4 py-2">
                      <Icon name={isLight ? 'MoonIcon' : 'SunIcon'} size={14} />
                      {isLight ? 'Dark Mode' : 'Light Mode'}
                    </button>
                  </div>
                </div>

                <div className="rounded-xl p-5" style={{ background: cardBg, border: `1px solid ${borderColor}` }}>
                  <h3 className="text-sm font-700 mb-4" style={{ color: headingText }}>Company Profile</h3>
                  {[
                    { label: 'Company Name', value: 'ProposalLink Technologies LLC' },
                    { label: 'Default Currency', value: 'OMR — Omani Rial' },
                    { label: 'Fiscal Year Start', value: 'January' },
                  ].map(({ label, value }) => (
                    <div key={label} className="flex items-center justify-between py-3" style={{ borderBottom: `1px solid ${borderColor}` }}>
                      <span className="text-sm" style={{ color: headingText }}>{label}</span>
                      <span className="text-sm font-600" style={{ color: '#1E90FF' }}>{value}</span>
                    </div>
                  ))}
                </div>
              </>
            )}

            {activeSection === 'margin' && (
              <div className="rounded-xl p-5" style={{ background: cardBg, border: `1px solid ${borderColor}` }}>
                <h3 className="text-sm font-700 mb-4" style={{ color: headingText }}>Margin Rules</h3>
                {[
                  { label: 'Minimum Margin Floor', value: '15%', desc: 'Proposals below this cannot be submitted' },
                  { label: 'Target Margin', value: '20%', desc: 'Default margin for new proposals' },
                  { label: 'Maximum Margin Cap', value: '40%', desc: 'Alert if margin exceeds this' },
                ].map(({ label, value, desc }) => (
                  <div key={label} className="flex items-center justify-between py-3" style={{ borderBottom: `1px solid ${borderColor}` }}>
                    <div>
                      <p className="text-sm font-600" style={{ color: headingText }}>{label}</p>
                      <p className="text-xs mt-0.5" style={{ color: mutedText }}>{desc}</p>
                    </div>
                    <span className="text-sm font-700 px-3 py-1 rounded-lg" style={{ background: 'rgba(30,144,255,0.1)', color: '#1E90FF' }}>{value}</span>
                  </div>
                ))}
              </div>
            )}

            {activeSection === 'libraries' && renderLibraries()}

            {activeSection === 'zoho' && renderZohoSection()}

            {activeSection === 'zoho-sync' && renderZohoSyncDashboard()}

            {activeSection === 'field-mapping' && renderFieldMapping()}

            {(activeSection === 'notifications' || activeSection === 'team' || activeSection === 'templates') && (
              <div className="rounded-xl p-8 text-center" style={{ background: cardBg, border: `1px solid ${borderColor}` }}>
                <Icon name="Cog6ToothIcon" size={32} style={{ color: mutedText, margin: '0 auto 12px' } as React.CSSProperties} />
                <p className="text-sm font-600" style={{ color: headingText }}>
                  {activeSection.charAt(0).toUpperCase() + activeSection.slice(1)} Settings
                </p>
                <p className="text-xs mt-1" style={{ color: mutedText }}>Configuration options coming soon.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
